import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  MessageCircle, Send, X, Phone, Video, MoreVertical,
  ChevronLeft, Search, User, Check, CheckCheck
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Chat bubble component
const ChatBubble = ({ message, isOwn }) => (
  <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-3`}>
    <div className={`max-w-[75%] px-4 py-2 rounded-2xl ${
      isOwn 
        ? 'bg-blue-600 text-white rounded-br-sm' 
        : 'bg-slate-700 text-white rounded-bl-sm'
    }`}>
      <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
      <div className={`flex items-center gap-1 mt-1 text-xs ${isOwn ? 'text-blue-200' : 'text-slate-400'}`}>
        <span>{new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
        {isOwn && (
          message.read ? <CheckCheck className="w-3 h-3" /> : <Check className="w-3 h-3" />
        )}
      </div>
    </div>
  </div>
);

// Conversation list item
const ConversationItem = ({ conversation, userId, onClick, isActive }) => {
  const otherParticipant = conversation.participants?.find(p => p.id !== userId) || {};
  
  return (
    <div 
      onClick={onClick}
      className={`flex items-center gap-3 p-3 cursor-pointer transition-colors ${
        isActive ? 'bg-slate-700' : 'hover:bg-slate-800'
      }`}
    >
      <div className="w-12 h-12 rounded-full bg-slate-600 flex items-center justify-center">
        <User className="w-6 h-6 text-slate-300" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-white truncate">
            {otherParticipant.name || 'Unknown'}
          </h4>
          {conversation.unread_count > 0 && (
            <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full">
              {conversation.unread_count}
            </span>
          )}
        </div>
        <p className="text-sm text-slate-400 truncate">{conversation.last_message}</p>
      </div>
    </div>
  );
};

// Main Chat Interface
export const ChatInterface = ({ 
  userId, 
  userName, 
  userType = 'dispatcher',
  onStartCall,
  onStartVideoCall,
  isOpen,
  onClose,
  initialRecipient = null
}) => {
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEndRef = useRef(null);
  const pollingRef = useRef(null);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    if (!userId) return;
    try {
      const response = await fetch(`${API}/messages/conversations/${userId}`);
      const data = await response.json();
      setConversations(data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  }, [userId]);

  // Fetch messages for active conversation
  const fetchMessages = useCallback(async () => {
    if (!activeConversation) return;
    try {
      const response = await fetch(`${API}/messages/conversation/${activeConversation.id}`);
      const data = await response.json();
      setMessages(data);
      
      // Mark as read
      await fetch(`${API}/messages/read/${activeConversation.id}/${userId}`, { method: 'PUT' });
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  }, [activeConversation, userId]);

  // Send message
  const sendMessage = async () => {
    if (!newMessage.trim() || !activeConversation) return;
    
    const otherParticipant = activeConversation.participants?.find(p => p.id !== userId);
    if (!otherParticipant) return;

    try {
      const response = await fetch(`${API}/messages/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sender_id: userId,
          sender_name: userName,
          sender_type: userType,
          recipient_id: otherParticipant.id,
          recipient_type: otherParticipant.type,
          content: newMessage.trim()
        })
      });
      
      if (response.ok) {
        setNewMessage('');
        fetchMessages();
        fetchConversations();
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  // Start new conversation
  const startNewConversation = async (recipient) => {
    const conversationId = `${Math.min(userId, recipient.id)}_${Math.max(userId, recipient.id)}`;
    
    // Check if conversation exists
    const existing = conversations.find(c => c.id === conversationId);
    if (existing) {
      setActiveConversation(existing);
    } else {
      // Create new conversation placeholder
      const newConv = {
        id: conversationId,
        participants: [
          { id: userId, name: userName, type: userType },
          { id: recipient.id, name: recipient.name, type: recipient.type }
        ],
        last_message: '',
        unread_count: 0
      };
      setActiveConversation(newConv);
    }
  };

  // Scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Fetch conversations on mount
  useEffect(() => {
    if (isOpen && userId) {
      fetchConversations();
    }
  }, [isOpen, userId, fetchConversations]);

  // Fetch messages when conversation changes
  useEffect(() => {
    if (activeConversation) {
      fetchMessages();
    }
  }, [activeConversation, fetchMessages]);

  // Polling for new messages
  useEffect(() => {
    if (isOpen && activeConversation) {
      pollingRef.current = setInterval(() => {
        fetchMessages();
        fetchConversations();
      }, 3000);
    }
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [isOpen, activeConversation, fetchMessages, fetchConversations]);

  // Handle initial recipient
  useEffect(() => {
    if (initialRecipient && isOpen) {
      startNewConversation(initialRecipient);
    }
  }, [initialRecipient, isOpen]);

  if (!isOpen) return null;

  const otherParticipant = activeConversation?.participants?.find(p => p.id !== userId);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <Card className="w-full max-w-4xl h-[600px] bg-slate-900 border-slate-700 flex overflow-hidden">
        {/* Conversations List */}
        <div className={`w-80 border-r border-slate-700 flex flex-col ${activeConversation ? 'hidden md:flex' : 'flex'}`}>
          <div className="p-4 border-b border-slate-700 flex items-center justify-between">
            <h3 className="font-semibold text-white">Messages</h3>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          <div className="p-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input 
                placeholder="Search conversations..." 
                className="pl-9 bg-slate-800 border-slate-700 text-white"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
          
          <ScrollArea className="flex-1">
            {conversations.length === 0 ? (
              <div className="p-4 text-center text-slate-400">
                <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No conversations yet</p>
              </div>
            ) : (
              conversations
                .filter(c => {
                  if (!searchQuery) return true;
                  const other = c.participants?.find(p => p.id !== userId);
                  return other?.name?.toLowerCase().includes(searchQuery.toLowerCase());
                })
                .map((conv) => (
                  <ConversationItem 
                    key={conv.id}
                    conversation={conv}
                    userId={userId}
                    onClick={() => setActiveConversation(conv)}
                    isActive={activeConversation?.id === conv.id}
                  />
                ))
            )}
          </ScrollArea>
        </div>

        {/* Chat Area */}
        <div className={`flex-1 flex flex-col ${!activeConversation ? 'hidden md:flex' : 'flex'}`}>
          {activeConversation ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-slate-700 flex items-center gap-3">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="md:hidden"
                  onClick={() => setActiveConversation(null)}
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <div className="w-10 h-10 rounded-full bg-slate-600 flex items-center justify-center">
                  <User className="w-5 h-5 text-slate-300" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-white">{otherParticipant?.name || 'Unknown'}</h4>
                  <p className="text-xs text-slate-400 capitalize">{otherParticipant?.type}</p>
                </div>
                <div className="flex gap-2">
                  {onStartCall && (
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => onStartCall(otherParticipant)}
                      className="text-slate-400 hover:text-white"
                    >
                      <Phone className="w-5 h-5" />
                    </Button>
                  )}
                  {onStartVideoCall && (
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => onStartVideoCall(otherParticipant)}
                      className="text-slate-400 hover:text-white"
                    >
                      <Video className="w-5 h-5" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Messages */}
              <ScrollArea className="flex-1 p-4">
                {messages.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-slate-400">
                    <p>Start a conversation</p>
                  </div>
                ) : (
                  <>
                    {messages.map((msg) => (
                      <ChatBubble 
                        key={msg.id}
                        message={msg}
                        isOwn={msg.sender_id === userId}
                      />
                    ))}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </ScrollArea>

              {/* Message Input */}
              <div className="p-4 border-t border-slate-700">
                <div className="flex gap-2">
                  <Input 
                    placeholder="Type a message..."
                    className="flex-1 bg-slate-800 border-slate-700 text-white"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                  />
                  <Button onClick={sendMessage} disabled={!newMessage.trim()}>
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-400">
              <div className="text-center">
                <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg">Select a conversation</p>
                <p className="text-sm">or start a new one from the Fleet page</p>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

// Chat button for nav bar
export const ChatButton = ({ userId, onClick }) => {
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const fetchUnread = async () => {
      if (!userId) return;
      try {
        const response = await fetch(`${API}/messages/unread-count/${userId}`);
        const data = await response.json();
        setUnreadCount(data.unread_messages || 0);
      } catch (error) {
        console.error('Error fetching unread count:', error);
      }
    };

    fetchUnread();
    const interval = setInterval(fetchUnread, 10000);
    return () => clearInterval(interval);
  }, [userId]);

  return (
    <Button 
      variant="ghost" 
      size="icon" 
      onClick={onClick}
      className="relative"
    >
      <MessageCircle className="w-5 h-5" />
      {unreadCount > 0 && (
        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </Button>
  );
};

export default ChatInterface;
